import { useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAccount } from 'wagmi'
import {
  TransactionCallback,
  TransactionCallbackStage,
} from '@lidofinance/lido-ethereum-sdk'
import { useNativeTokenBalance } from '~/state/wallet/hooks'
import {
  useLidoSDK,
  useWstEthBalance,
  useWstEth2StEthRate,
  useUnwrapGasFee,
  useUnwrapReceiveAmount,
  useIsValidNetwork,
} from '../hooks'
import {
  useModalState,
  useModalToggle,
  useApprovalModalStateDispatch,
} from '~/state/application/hooks'
import { ApplicationModal } from '~/state/application/reducer'
import { ApprovalProgress } from '~/components/ApprovalModal/ApproveStatusLabel'
import { parseUnits } from 'viem'
import TokenInput from '../components/TokenInput'
import PrimaryButton from '~/components/Button/PrimaryButton'
import OrderProgress from '../components/OrderProgress'
import MaxButton from '~/components/Button/MaxButton'
import { wstETH } from '~/constants/tokens'
import { formatFormAmount, toBigNumber, gt } from '~/utils/numbers'
import { RESET_STAGE_TIME } from '../constants'
import {
  StyledStakeSection,
  StyledStakeDescription,
  StyledStakeForm,
  StyledFormGroup,
  StyledLabel,
  StyledBalanceInfo,
  StyledReceiveInfo,
  StyledInfoRow,
  StyledInfoLabel,
  StyledInfoValue,
} from '../styled'
import ImgLidoUnwrapFlow from '~/assets/images/lido-unwrap-flow.png'
import ImgTokenWstETH from '~/assets/images/token-wsteth-with-bg.png'
import NotificationBar from '~/components/NotificationBar'
import { useIsDisableTrade } from '~/state/dex/hooks'
import { useGA } from '~/hooks'
import { getIsUserCancel } from '../utils'

const UnWrapForm = () => {
  const { t } = useTranslation()
  const { trackView, trackClick } = useGA()
  const { address: account } = useAccount()
  const isWalletConnected = !!account
  const [amount, setAmount] = useState('')
  const [
    currentStage,
    setCurrentStage,
  ] = useState<TransactionCallbackStage | null>(null)
  const [showOrderProgress, setShowOrderProgress] = useState(false)
  const wstEthBalance = useWstEthBalance()
  const formattedWstEthBalance = wstEthBalance?.toDecimalsFixed()
  const ethBalance = useNativeTokenBalance()
  const formattedEthBalance = ethBalance?.toDecimalsFixed()
  const isValidNetwork = useIsValidNetwork()

  const { wrap } = useLidoSDK()
  const { rate: wstEth2StEthRate } = useWstEth2StEthRate('1')
  const { gasFee, gasFeeUSD } = useUnwrapGasFee(amount)
  const receiveAmount = useUnwrapReceiveAmount(amount)

  const isInsufficientBalance = useMemo(() => {
    return gt(amount, formattedWstEthBalance)
  }, [amount, formattedWstEthBalance])

  const isInsufficientETHForGas = useMemo(() => {
    if (!amount || !formattedEthBalance || !gasFee) {
      return false
    }
    const ethBalanceBN = toBigNumber(formattedEthBalance)
    const gasFeeRequired = toBigNumber(gasFee)
    return gt(gasFeeRequired, ethBalanceBN)
  }, [amount, formattedEthBalance, gasFee])

  const { progress } = useModalState(ApplicationModal.APPROVAL_STATUS)
  const toggleSubmitParamsModal = useModalToggle(
    ApplicationModal.APPROVAL_STATUS,
    {
      stack: true,
      modalState: {
        title: t('submitParamsModal.title'),
        description1: t('submitParamsModal.description_1'),
        description2: t('submitParamsModal.description_2'),
        showLogo: false,
        progress: ApprovalProgress.APPROVING,
      },
    },
  )
  const toggleWalletModal = useModalToggle(ApplicationModal.WALLET_CONNECT, {
    modalState: { from: 'lido_wrap' },
  })
  const toggleSwitchNetworkModal = useModalToggle(
    ApplicationModal.SWITCH_NETWORK,
  )
  const updateApprovalState = useApprovalModalStateDispatch()

  const isDisableTrade = useIsDisableTrade()
  const toggleIpBlockedModal = useModalToggle(ApplicationModal.IP_BLOCKED)

  const handleMaxClick = () => {
    setAmount(formattedWstEthBalance)
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    // 最多输入 6 位小数
    if (value === '' || /^(\d+)(\.\d{0,6})?$/.test(value)) {
      setAmount(value)
    }
  }

  const handleBlur = () => {
    if (!amount) return
    // 去掉前导零
    let [int, dec] = amount.split('.')
    int = String(Number(int))
    setAmount(dec !== undefined ? `${int}.${dec}` : int)
  }

  const unwrapValueRef = useRef<string>('0')
  const showOrderProgressRef = useRef(showOrderProgress)
  showOrderProgressRef.current = showOrderProgress

  const txCallback: TransactionCallback = ({ stage }) => {
    const targetStage = [
      TransactionCallbackStage.RECEIPT,
      TransactionCallbackStage.DONE,
      TransactionCallbackStage.MULTISIG_DONE,
      TransactionCallbackStage.ERROR,
    ]
    if (targetStage.includes(stage)) {
      setCurrentStage(stage)
    }

    switch (stage) {
      case TransactionCallbackStage.RECEIPT:
        trackClick('webtl_stk_unwrap_submit', {
          symbol: wstETH.symbol,
          amount: unwrapValueRef.current,
        })
        updateApprovalState({
          progress: ApprovalProgress.APPROVE_SUCCESS,
        })
        setShowOrderProgress(true)
        break
      case TransactionCallbackStage.DONE:
      case TransactionCallbackStage.MULTISIG_DONE:
        // 使用 Safe Wallet 时没有 RECEIPT 阶段，直接从 SIGN 阶段跳到 MULTISIG_DONE 阶段
        if (
          progress !== ApprovalProgress.APPROVE_SUCCESS &&
          !showOrderProgressRef.current
        ) {
          trackClick('webtl_stk_unwrap_submit', {
            symbol: wstETH.symbol,
            amount: unwrapValueRef.current,
          })
          updateApprovalState({
            progress: ApprovalProgress.APPROVE_SUCCESS,
          })
          setShowOrderProgress(true)
        }
        trackClick('webtl_stk_unwrap_confirm', {
          symbol: wstETH.symbol,
          amount: unwrapValueRef.current,
          is_success: true,
        })
        unwrapValueRef.current = '0'
        setTimeout(() => {
          setAmount('')
          setShowOrderProgress(false)
          setCurrentStage(null)
        }, RESET_STAGE_TIME)
        break
      default:
        break
    }
  }

  const handleUnwrap = async () => {
    setCurrentStage(TransactionCallbackStage.SIGN)
    const value = parseUnits(amount, wstETH.decimals)
    unwrapValueRef.current = amount

    try {
      toggleSubmitParamsModal()

      await wrap.unwrap({
        value,
        callback: txCallback,
      })
    } catch (error) {
      const isUserCancel = getIsUserCancel(error)
      if (isUserCancel) {
        trackView('webtl_stk_unwrap_confirm_cancel')
      } else {
        trackClick('webtl_stk_unwrap_confirm', {
          symbol: wstETH.symbol,
          amount: unwrapValueRef.current,
          is_success: false,
          message: error.message,
        })
      }
      unwrapValueRef.current = '0'
      updateApprovalState({
        progress: ApprovalProgress.APPROVE_REJECTED,
      })
      setTimeout(() => {
        setShowOrderProgress(false)
        setCurrentStage(null)
      }, RESET_STAGE_TIME)
    }
  }

  const handleConnectWallet = () => {
    trackClick('webtl_walletconnect', {
      from: 'lido_wrap',
    })
    toggleWalletModal()
  }

  const handleClick = () => {
    if (isDisableTrade) {
      toggleIpBlockedModal()
    } else if (!isWalletConnected) {
      handleConnectWallet()
    } else if (!isValidNetwork) {
      toggleSwitchNetworkModal()
    } else {
      handleUnwrap()
    }
  }

  const buttonText = useMemo(() => {
    if (!isWalletConnected) {
      return t('connect_wallet')
    }
    if (!isValidNetwork) {
      return t('switch_network')
    }
    if (isInsufficientBalance) {
      return t('preview_$symbol_insufficient_balance', {
        symbol: 'wstETH',
      })
    }
    if (isInsufficientETHForGas) {
      return t('preview_$symbol_insufficient_balance', { symbol: 'ETH' })
    }
    if (currentStage === null) {
      return t('unwrap')
    }
    return t('waiting_for_confirm')
  }, [
    isWalletConnected,
    isValidNetwork,
    isInsufficientBalance,
    isInsufficientETHForGas,
    currentStage,
    t,
  ])

  const buttonDisabled = useMemo(() => {
    return (
      isWalletConnected &&
      isValidNetwork &&
      (!Number(amount) ||
        isInsufficientBalance ||
        isInsufficientETHForGas ||
        currentStage === TransactionCallbackStage.SIGN)
    )
  }, [
    isWalletConnected,
    isValidNetwork,
    isInsufficientBalance,
    isInsufficientETHForGas,
    amount,
    currentStage,
  ])

  return (
    <StyledStakeSection>
      <StyledStakeDescription>
        <img
          src={ImgLidoUnwrapFlow.src}
          width={80}
          height={28}
          alt="unwrap flow"
        />
        <div className="description-text">{t('lido_unwrap_description')}</div>
      </StyledStakeDescription>

      <StyledStakeForm>
        <StyledFormGroup>
          {isWalletConnected && (
            <StyledLabel>
              <StyledBalanceInfo>
                {`${t('balance')}: ${formatFormAmount(
                  formattedWstEthBalance ?? '~',
                  4,
                )} ${wstETH.symbol}`}
                <MaxButton handleClick={handleMaxClick} />
              </StyledBalanceInfo>
            </StyledLabel>
          )}
          <TokenInput
            amount={amount}
            token={wstETH}
            handleInputChange={handleInputChange}
            handleBlur={handleBlur}
          />
        </StyledFormGroup>

        <StyledReceiveInfo>
          <StyledInfoRow>
            <StyledInfoLabel>{t('order_receive')}</StyledInfoLabel>
            <StyledInfoValue>
              {receiveAmount} stETH{' '}
              <img
                src={ImgTokenWstETH.src}
                alt="wstETH"
                width={20}
                height={20}
              />
            </StyledInfoValue>
          </StyledInfoRow>
          <StyledInfoRow>
            <StyledInfoLabel>{t('rate')}</StyledInfoLabel>
            <StyledInfoValue>
              {`1 wstETH = ${wstEth2StEthRate} stETH`}
            </StyledInfoValue>
          </StyledInfoRow>
          <StyledInfoRow>
            <StyledInfoLabel>{t('estimate_gas_fee')}</StyledInfoLabel>
            <StyledInfoValue>{`~ $${gasFeeUSD}`}</StyledInfoValue>
          </StyledInfoRow>
        </StyledReceiveInfo>

        {showOrderProgress && <OrderProgress stage={currentStage!} />}
        {!showOrderProgress && (
          <>
            {!isValidNetwork && (
              <NotificationBar
                level="warning"
                text={t('unmatched_network_swap_warning')}
                showLink={false}
                showClose={false}
              />
            )}
            <PrimaryButton
              text={buttonText}
              disabled={buttonDisabled}
              loading={currentStage === TransactionCallbackStage.SIGN}
              handleClick={handleClick}
            />
          </>
        )}
      </StyledStakeForm>
    </StyledStakeSection>
  )
}

export default UnWrapForm
